import { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Gift, Users, Copy, Check, Share2 } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ReferralProgram() {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [referralCode, setReferralCode] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!email || !email.includes("@")) {
      setError("Please enter a valid email address");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/referral/generate`, { email });
      setReferralCode(response.data.referral_code);
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const copyToClipboard = () => {
    const referralLink = `https://www.dreammakersstudios.com.au?ref=${referralCode}`;
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const shareReferral = () => {
    const referralLink = `https://www.dreammakersstudios.com.au?ref=${referralCode}`;
    const text = `Check out Dream Makers Recording Group! Use my referral link and we both get $50 off: ${referralLink}`;
    
    if (navigator.share) {
      navigator.share({
        title: "Dream Makers Recording Group - Referral",
        text: text,
        url: referralLink,
      });
    } else {
      copyToClipboard();
    }
  };

  return (
    <section className="py-16 bg-gradient-to-b from-dream-bg to-dream-surface relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-10 right-10 w-72 h-72 bg-dream-violet rounded-full blur-3xl" />
        <div className="absolute bottom-10 left-10 w-96 h-96 bg-pink-500 rounded-full blur-3xl" />
      </div>

      <div className="max-w-4xl mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-8"
        >
          <div className="inline-flex items-center gap-2 bg-green-500/20 text-green-400 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Gift className="w-4 h-4" />
            Referral Program
          </div>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-3">
            Refer a Friend, Get $50 Off
          </h2>
          <p className="text-white/60 max-w-xl mx-auto">
            Share the Dream Makers experience! When your friend books their first session, you both get $50 off your next booking.
          </p>
        </motion.div>

        {/* How it works */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="grid md:grid-cols-3 gap-6 mb-10"
        >
          <div className="bg-dream-surface/50 border border-dream-border rounded-xl p-6 text-center">
            <div className="w-12 h-12 bg-dream-violet/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-dream-violet font-bold text-xl">1</span>
            </div>
            <h3 className="text-white font-semibold mb-2">Get Your Link</h3>
            <p className="text-white/60 text-sm">Enter your email to generate your unique referral link</p>
          </div>
          <div className="bg-dream-surface/50 border border-dream-border rounded-xl p-6 text-center">
            <div className="w-12 h-12 bg-pink-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-pink-500 font-bold text-xl">2</span>
            </div>
            <h3 className="text-white font-semibold mb-2">Share It</h3>
            <p className="text-white/60 text-sm">Send your link to friends who love music</p>
          </div>
          <div className="bg-dream-surface/50 border border-dream-border rounded-xl p-6 text-center">
            <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-green-500 font-bold text-xl">3</span>
            </div>
            <h3 className="text-white font-semibold mb-2">Both Save $50</h3>
            <p className="text-white/60 text-sm">When they book, you both get $50 off</p>
          </div>
        </motion.div>

        {/* Form / Referral Code Display */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="max-w-md mx-auto"
        >
          {!referralCode ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-dream-bg border-dream-border text-white placeholder:text-white/40 flex-1"
                  required
                />
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-dream-violet hover:bg-dream-violet-glow px-6"
                >
                  {isSubmitting ? "..." : <Users className="w-5 h-5" />}
                </Button>
              </div>
              {error && <p className="text-red-400 text-sm text-center">{error}</p>}
            </form>
          ) : (
            <div className="space-y-4">
              <div className="bg-dream-bg border border-dream-border rounded-xl p-4">
                <p className="text-white/60 text-sm mb-2">Your Referral Code:</p>
                <div className="flex items-center gap-2">
                  <code className="bg-dream-surface px-4 py-2 rounded-lg text-dream-violet font-mono flex-1">
                    {referralCode}
                  </code>
                  <Button
                    onClick={copyToClipboard}
                    variant="outline"
                    className="border-dream-border text-white"
                  >
                    {copied ? <Check className="w-5 h-5 text-green-500" /> : <Copy className="w-5 h-5" />}
                  </Button>
                </div>
              </div>
              
              <Button
                onClick={shareReferral}
                className="w-full bg-gradient-to-r from-dream-violet to-pink-500 hover:from-dream-violet-glow hover:to-pink-400 py-6"
              >
                <Share2 className="w-5 h-5 mr-2" />
                Share Referral Link
              </Button>

              <p className="text-white/40 text-xs text-center">
                Your friend must mention your referral code when booking to redeem the discount.
              </p>
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
}
